// PlayerLoginPage — phone OTP login (US-only at launch).
//
// Two steps:
//   1. Phone entry      → POST signInWithOtp → Supabase Auth sends SMS via Twilio
//   2. 6-digit code     → POST verifyOtp     → Supabase session established
//
// On verified session, AuthContext refreshes the player row from
// get-my-player. The auth gate in App.jsx then routes the user to:
//   - SignupPage if the player row is missing or incomplete
//   - HomePage if the player row exists with tos_accepted_at IS NOT NULL
//
// Visual redesign 2026-07-05: page uses the shell OTP design system
// (docs/design/login-otp-flow.html). Styles live in src/styles/shell-otp.css;
// this file is markup + copy + state only.

import { useEffect, useRef, useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useTheme } from '../contexts/ThemeContext'
import { useShellStatusBar } from '../lib/statusBar'
import { enabledSocialProviders, socialButtonStyle } from '../lib/socialProviders'
import SocialGlyph from '../components/SocialGlyph'

// US-only E.164 normalizer. Returns +1XXXXXXXXXX or null.
function normalizeUsPhone(raw) {
  const digits = (raw || '').replace(/\D/g, '')
  if (digits.length === 10) return '+1' + digits
  if (digits.length === 11 && digits.startsWith('1')) return '+' + digits
  return null
}

function formatUsPhone(raw) {
  const d = (raw || '').replace(/\D/g, '').slice(0, 10)
  if (d.length === 0) return ''
  if (d.length < 4) return `(${d}`
  if (d.length < 7) return `(${d.slice(0, 3)}) ${d.slice(3)}`
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`
}

// Resend cooldown — matches Twilio Verify's typical minimum resend spacing.
// When the countdown hits 0 the button becomes clickable again.
const RESEND_SECONDS = 30

function formatCountdown(s) {
  const m = Math.floor(s / 60)
  const rem = s % 60
  return `${m}:${String(rem).padStart(2, '0')}`
}

export default function PlayerLoginPage() {
  const { signInWithPhone, verifyOtp, signInWithOAuth } = useAuth()
  const { theme } = useTheme()

  // This shell screen paints a fixed dark Matte Navy background regardless of
  // the golf theme, so force LIGHT (Android) status-bar icons while it is up.
  useShellStatusBar(theme)

  const [step, setStep] = useState('phone')   // 'phone' | 'otp'
  const [phoneInput, setPhoneInput] = useState('')
  const [normalized, setNormalized] = useState('')
  const [code, setCode] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [resendIn, setResendIn] = useState(0)

  // Focus the right input on each step transition so keyboard opens.
  const phoneInputRef = useRef(null)
  const otpInputRef = useRef(null)
  useEffect(() => {
    if (step === 'phone') phoneInputRef.current?.focus()
    if (step === 'otp')   otpInputRef.current?.focus()
  }, [step])

  // Resend countdown ticker. Runs only on the OTP step and only when
  // resendIn > 0. Cleans up its own interval on unmount / step change.
  useEffect(() => {
    if (step !== 'otp' || resendIn <= 0) return
    const id = setInterval(() => setResendIn(s => (s > 0 ? s - 1 : 0)), 1000)
    return () => clearInterval(id)
  }, [step, resendIn])

  const sendCode = async () => {
    const norm = normalizeUsPhone(phoneInput)
    if (!norm) {
      setError('Enter a 10-digit US phone number.')
      return
    }
    setError('')
    setLoading(true)
    const err = await signInWithPhone(norm)
    setLoading(false)
    if (err) {
      setError(err)
    } else {
      setNormalized(norm)
      setCode('')
      setStep('otp')
      setResendIn(RESEND_SECONDS)
    }
  }

  const verify = async () => {
    if (code.length < 6) return
    setError('')
    setLoading(true)
    const err = await verifyOtp(normalized, code)
    setLoading(false)
    if (err) {
      setError(err)
      setCode('')
    }
    // On success the auth gate re-renders this away.
  }

  const resend = async () => {
    if (resendIn > 0 || loading) return
    setError('')
    setLoading(true)
    const err = await signInWithPhone(normalized)
    setLoading(false)
    if (err) setError(err)
    else setResendIn(RESEND_SECONDS)
  }

  const useDifferentNumber = () => {
    setStep('phone')
    setError('')
    setCode('')
  }

  // Social sign-in. Redirects to the provider; the auth gate takes over on
  // return. Phone OTP stays the primary path — these are additive so a user
  // whose social account carries a profile picture can sign in with it.
  const continueWithOAuth = async (provider) => {
    if (loading) return
    setError('')
    setLoading(true)
    const err = await signInWithOAuth(provider)
    // On success the browser navigates away to the provider, so we only reach
    // here (and clear loading) if the redirect could not be started.
    if (err) {
      setError(err)
      setLoading(false)
    }
  }

  // Which social providers render is driven entirely by the shared
  // ENABLED_SOCIAL list (src/lib/socialProviders.js) — today Facebook + X.
  // Google is wired but hidden (D-283); re-enabling it is a one-line list edit.
  const social = enabledSocialProviders()
  const socialButtons = social.length > 0 ? (
    <>
      <div
        aria-hidden="true"
        style={{
          textAlign: 'center',
          fontSize: '0.75rem',
          textTransform: 'uppercase',
          letterSpacing: '0.15em',
          opacity: 0.5,
          margin: '18px 0 12px',
        }}
      >
        or connect a social account
      </div>
      {social.map((sp, i) => {
        return (
          <button
            key={sp.provider}
            className="btn btn-full"
            onClick={() => continueWithOAuth(sp.provider)}
            disabled={loading}
            style={{
              ...socialButtonStyle(sp.provider),
              ...(i < social.length - 1 ? { marginBottom: 8 } : {}),
            }}
          >
            <SocialGlyph provider={sp.provider} />
            {sp.loginLabel}
          </button>
        )
      })}
    </>
  ) : null

  const errorLine = error ? (
    <p style={{
      color: '#F5B5A9',
      fontSize: '0.85rem',
      textAlign: 'center',
      marginTop: 12,
    }}>
      {error}
    </p>
  ) : null

  if (step === 'phone') {
    return (
      <div className="page otp-page" data-shell="tournyflex">
        <div className="wm">
          <img src="/tf-shield.webp" alt="TournyFlex" className="shield" />
          <span className="type-fallback">TournyFlex</span>
        </div>

        <p className="eyebrow">Sign in</p>
        <h1 className="h1">Your phone</h1>
        <p className="lede">
          We'll text you a 6-digit code to sign in. Message and data rates may apply. Reply STOP to opt out of non-verification messages.
        </p>

        <div className="phone-field">
          <div className="prefix">+1</div>
          <input
            ref={phoneInputRef}
            className="num"
            type="tel"
            inputMode="numeric"
            autoComplete="tel"
            value={formatUsPhone(phoneInput)}
            onChange={e => setPhoneInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && sendCode()}
            placeholder="(555) 123-4567"
            disabled={loading}
          />
        </div>

        {errorLine}

        <button
          className="btn btn-primary"
          onClick={sendCode}
          disabled={loading || !normalizeUsPhone(phoneInput)}
        >
          {loading ? 'Sending…' : 'Send code'}
        </button>

        {socialButtons}

        <p className="fineprint">
          By continuing you agree to the{' '}
          <a href="/terms" target="_blank" rel="noopener noreferrer">Terms</a>
          {' '}and{' '}
          <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
        </p>

        <div className="legal">© 2026 TournyFlex LLC</div>
      </div>
    )
  }

  // OTP step
  return (
    <div className="page otp-page" data-shell="tournyflex">
      <div className="wm">
        <img src="/tf-shield.webp" alt="TournyFlex" className="shield" />
        <span className="type-fallback">TournyFlex</span>
      </div>

      <p className="eyebrow">Enter code</p>
      <h1 className="h1">Check your texts</h1>
      <p className="lede">
        Sent to{' '}
        <span className="phone-inline">
          {formatUsPhone(normalized.replace(/^\+1/, ''))}
        </span>
      </p>

      <input
        ref={otpInputRef}
        className="otp-field"
        type="tel"
        inputMode="numeric"
        autoComplete="one-time-code"
        maxLength={6}
        value={code}
        onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
        onKeyDown={e => e.key === 'Enter' && verify()}
        placeholder="- - - - - -"
        disabled={loading}
      />

      {errorLine}

      <button
        className="btn btn-primary"
        onClick={verify}
        disabled={loading || code.length < 6}
      >
        {loading ? 'Verifying…' : 'Verify'}
      </button>

      <button
        className="btn btn-ghost"
        onClick={resend}
        disabled={loading || resendIn > 0}
      >
        {resendIn > 0 ? `Resend code (${formatCountdown(resendIn)})` : 'Resend code'}
      </button>

      <button
        className="btn btn-ghost subtle"
        onClick={useDifferentNumber}
        disabled={loading}
      >
        Use a different number
      </button>

      {resendIn > 0 && (
        <p className="resend-line">
          <span className="live-dot"></span>
          Code active · resend in {formatCountdown(resendIn)}
        </p>
      )}

      <div className="legal">© 2026 TournyFlex LLC</div>
    </div>
  )
}
